/**
 * A film's page (`/sheds/film/` and each beside it): the film, what happens in it in
 * words, the way on to the designer, and the plan type's other films. A how-to
 * (`/sheds/how-to/…`) plays on the same page, which says what it covers rather than what
 * happens, and leads on to the reference as well.
 *
 * The film starts itself the first time most of it is on screen, and never for a reader
 * who has asked for less motion — they get its last frame, the end card, and a play
 * button. It stops when it is scrolled away, and it plays once rather than looping:
 * anything that moves for more than five seconds on its own needs a way to stop it, and
 * a film that ends has one built in.
 *
 * The sound (`speaker.ts`) is off until the viewer turns it on: browsers will not start
 * it any other way, and a page should not make noise nobody asked for.
 *
 * `#/record` adds the recorder (`record.ts`): the same film, drawn at an ad's size and
 * saved as a video, with its soundtrack or without. It is not hidden, because there is
 * nothing to hide — anybody may save our ad — but nothing links to it.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import { REFERENCES } from "../reference/references.js";
import { useLocation } from "../routing.js";
import type { PlanTypeInfo } from "../site/planTypes.js";
import { SiteBar, SiteFoot } from "../ui/SiteBar.js";
import type { Film, Site } from "./film.js";
import { FILMS, type FilmKind } from "./films.js";
import type { Palette } from "./palette.js";
import { Player } from "./player.js";
import { FORMATS, fileName, recordFilm, recordingType, stillFrame } from "./record.js";
import { FULL_MIX, encodeWav, renderSoundtrack, type SoundOptions } from "./sound.js";
import { Speaker } from "./speaker.js";

export interface FilmPageProps {
  type: PlanTypeInfo;
  /** The page's own address, for the header and footer to mark. */
  path: string;
  kind: FilmKind;
  film: Film;
  palette: Palette;
  site: Site;
}

/** "a thirty second film", "a six minute how-to": what the page plays, by its length. */
export function lengthWords(kind: FilmKind, seconds: number): string {
  if (kind === "film") {
    return "a thirty second film";
  }
  return `a ${Math.round(seconds / 60)} minute how-to`;
}

/** "0:07", for a time in seconds. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function prefersLessMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function FilmPage({ type, path, kind, film, palette, site }: FilmPageProps): ReactElement {
  const location = useLocation();
  const [player, setPlayer] = useState<Player>();
  const beats = film.beats(site);
  const here = path;
  const others = FILMS.filter((entry) => entry.type === type.id && entry.kind === kind && entry.path !== path);
  const howTo = kind === "how-to";
  // A how-to leads on to the part of the reference it teaches, when the reference links it.
  const taught = REFERENCES[type.id].watch.find((watch) => watch.path === path)?.section;
  const reference = taught === undefined ? type.referencePath : `${type.referencePath}#${taught}`;
  return (
    <>
      <SiteBar here={here} />
      <main className="page film-page">
        <section className="hero film-hero">
          <p className="eyebrow">
            {type.name} · {lengthWords(kind, film.duration)}
          </p>
          <h1>{film.title}</h1>
          <p className="lead">{film.lead(site)}</p>
        </section>
        <FilmPlayer film={film} palette={palette} site={site} onReady={setPlayer} />
        <p className="actions">
          <a className="cta" href={type.designerPath}>
            Design a {type.singular}
          </a>{" "}
          <a className="text-link" href={type.catalogPath}>
            Start from a ready-made design
          </a>
          {howTo ? (
            <>
              {" "}
              <a className="text-link" href={reference}>
                {type.name} terms explained
              </a>
            </>
          ) : null}
        </p>
        <section aria-labelledby="story">
          <h2 id="story">{howTo ? "What it covers" : "What happens"}</h2>
          <ol className="film-beats">
            {beats.map((beat) => (
              <li key={beat.at}>
                <button
                  type="button"
                  className="film-seek"
                  onClick={() => {
                    player?.seek(beat.at);
                    player?.play();
                  }}
                  aria-label={`Play from ${clock(beat.at)}`}
                >
                  {clock(beat.at)}
                </button>
                <p>
                  {beat.caption === undefined ? null : <strong>{beat.caption} </strong>}
                  {beat.action}
                </p>
              </li>
            ))}
          </ol>
        </section>
        {others.length === 0 ? null : (
          <section aria-labelledby="more-films">
            <h2 id="more-films">
              More {type.singular} {howTo ? "how-tos" : "films"}
            </h2>
            <ul className="film-more">
              {others.map((other) => (
                <li key={other.path}>
                  <a className="text-link" href={other.path}>
                    {other.film.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
        {location.path === "record" ? <Recorder film={film} palette={palette} site={site} /> : null}
      </main>
      <SiteFoot here={here} />
    </>
  );
}

function FilmPlayer({
  film,
  palette,
  site,
  onReady,
}: {
  film: Film;
  palette: Palette;
  site: Site;
  onReady(player: Player): void;
}): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [player, setPlayer] = useState<Player>();
  const [speaker, setSpeaker] = useState<Speaker>();
  const [sound, setSound] = useState<"off" | "loading" | "on">("off");
  const [, setTick] = useState(0);

  useEffect(() => {
    const element = canvas.current;
    if (element === null) {
      return undefined;
    }
    const created = new Player(element, film, palette, site);
    const calm = prefersLessMotion();
    if (calm) {
      created.seek(film.duration);
    }
    setPlayer(created);
    onReady(created);
    const unsubscribe = created.subscribe(() => setTick((n) => n + 1));
    const speaks = film.score === undefined ? undefined : new Speaker(created, film, site);
    setSpeaker(speaks);
    setSound("off");

    // Start once, the first time most of the film is in view; stop when it leaves.
    let started = calm;
    let pausedByScroll = false;
    const watcher = new IntersectionObserver(
      ([entry]) => {
        if (entry === undefined) {
          return;
        }
        if (entry.intersectionRatio >= 0.6) {
          if (!started || pausedByScroll) {
            started = true;
            pausedByScroll = false;
            created.play();
          }
        } else if (entry.intersectionRatio < 0.2 && created.isPlaying) {
          pausedByScroll = true;
          created.pause();
        }
      },
      { threshold: [0, 0.2, 0.6, 1] },
    );
    watcher.observe(element);
    return () => {
      watcher.disconnect();
      unsubscribe();
      speaks?.dispose();
      created.dispose();
    };
  }, [film, palette, site, onReady]);

  const time = player?.time ?? 0;
  const playing = player?.isPlaying ?? false;
  const ended = player?.ended ?? false;
  return (
    <div className="film-player">
      <div className="film-stage">
        <canvas
          ref={canvas}
          role="img"
          aria-label={film.summary}
          onClick={() => player?.toggle()}
        />
        {playing ? null : (
          <button
            type="button"
            className="film-big"
            onClick={() => player?.play()}
            aria-label={ended ? "Watch again" : "Play the film"}
          >
            {ended ? "↻" : "▶"}
          </button>
        )}
      </div>
      <div className="film-controls">
        <button
          type="button"
          className="button"
          onClick={() => player?.toggle()}
          aria-label={playing ? "Pause" : ended ? "Watch again" : "Play"}
        >
          {playing ? "❚❚ Pause" : ended ? "↻ Again" : "▶ Play"}
        </button>
        <input
          type="range"
          min={0}
          max={film.duration}
          step={0.01}
          value={time}
          aria-label="Position in the film"
          onChange={(event) => {
            player?.pause();
            player?.seek(Number(event.target.value));
          }}
        />
        <span className="film-time">
          {clock(time)} / {clock(film.duration)}
        </span>
        {speaker === undefined ? null : (
          <button
            type="button"
            className="button film-sound"
            aria-pressed={sound === "on"}
            disabled={sound === "loading"}
            onClick={() => {
              if (sound === "on") {
                speaker.disable();
                setSound("off");
                return;
              }
              setSound("loading");
              speaker
                .enable()
                .then(() => setSound(speaker.enabled ? "on" : "off"))
                .catch(() => setSound("off"));
            }}
          >
            {sound === "on" ? "🔊 Sound on" : sound === "loading" ? "Sound…" : "🔇 Sound off"}
          </button>
        )}
      </div>
    </div>
  );
}

/** What a recording's soundtrack has in it. */
const MIXES = {
  full: { label: "Effects and music", options: FULL_MIX },
  effects: { label: "Effects only (lay your own music under it)", options: { effects: true, music: false } },
  none: { label: "No sound", options: undefined },
} satisfies Record<string, { label: string; options: SoundOptions | undefined }>;

/** The recorder: pick a shape, play the film into a file, save it. */
function Recorder({ film, palette, site }: { film: Film; palette: Palette; site: Site }): ReactElement {
  const [formatId, setFormatId] = useState(FORMATS[1]!.id);
  const [fps, setFps] = useState(30);
  const [mixId, setMixId] = useState<keyof typeof MIXES>("full");
  const [host, setHost] = useState(site.host);
  const [progress, setProgress] = useState<number>();
  const [saved, setSaved] = useState<{ href: string; name: string }>();
  const [still, setStill] = useState(film.duration);
  const [problem, setProblem] = useState<string>();
  const abort = useRef<AbortController>(undefined);
  const scored = film.score !== undefined;
  const sound = scored ? MIXES[mixId].options : undefined;
  const type = useMemo(() => recordingType(sound !== undefined), [sound]);
  const format = FORMATS.find((each) => each.id === formatId) ?? FORMATS[0]!;
  const recordingSite: Site = { ...site, host: host.trim() };

  useEffect(() => () => {
    if (saved !== undefined) {
      URL.revokeObjectURL(saved.href);
    }
  }, [saved]);

  const offer = (blob: Blob, name: string): void => {
    setSaved({ href: URL.createObjectURL(blob), name });
  };

  return (
    <section className="film-record" aria-labelledby="record">
      <h2 id="record">Record this film</h2>
      <p>
        Draws the film at the size an ad needs and saves it as{" "}
        {type === undefined
          ? "a video — but this browser cannot record one"
          : type.startsWith("video/mp4")
            ? sound === undefined
              ? "an H.264 MP4, which every ad platform takes"
              : "an H.264 MP4 with AAC sound, which every ad platform takes"
            : "a WebM, because this browser cannot write H.264; Chrome or Edge can"}
        . It plays in real time, so it takes as long as the film runs ({clock(film.duration)}): keep this tab in front until it is done.
      </p>
      <div className="film-record-fields">
        <label>
          Shape
          <select value={formatId} onChange={(event) => setFormatId(event.target.value)}>
            {FORMATS.map((each) => (
              <option key={each.id} value={each.id}>
                {each.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Frames a second
          <select value={fps} onChange={(event) => setFps(Number(event.target.value))}>
            <option value={30}>30</option>
            <option value={60}>60</option>
          </select>
        </label>
        {scored ? (
          <label>
            Sound
            <select value={mixId} onChange={(event) => setMixId(event.target.value as keyof typeof MIXES)}>
              {Object.entries(MIXES).map(([id, each]) => (
                <option key={id} value={id}>
                  {each.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          Address on the end card
          <input value={host} onChange={(event) => setHost(event.target.value)} placeholder="Left off when empty" />
        </label>
      </div>
      <p className="film-record-actions">
        <button
          type="button"
          className="button primary"
          disabled={type === undefined || progress !== undefined}
          onClick={() => {
            setProblem(undefined);
            setSaved(undefined);
            const controller = new AbortController();
            abort.current = controller;
            setProgress(0);
            recordFilm(film, palette, recordingSite, format, fps, sound, setProgress, controller.signal)
              .then((recording) => {
                offer(recording.blob, fileName(site, film, format, recording.extension));
                if (recording.late > recording.frames * 0.01) {
                  setProblem(
                    `This computer fell behind on ${recording.late} of ${recording.frames} frames, so the video ` +
                      "will stutter in places. Close other tabs and windows and record it again, or try 30 frames a second.",
                  );
                }
              })
              .catch((error: unknown) => {
                if (!(error instanceof DOMException && error.name === "AbortError")) {
                  setProblem(error instanceof Error ? error.message : "The recording failed.");
                }
              })
              .finally(() => setProgress(undefined));
          }}
        >
          {progress === undefined ? "Record video" : `Recording… ${Math.round(progress * 100)}%`}
        </button>
        {progress === undefined ? null : (
          <button type="button" className="button" onClick={() => abort.current?.abort()}>
            Stop
          </button>
        )}
      </p>
      <p className="film-record-actions">
        <label>
          Still at{" "}
          <input
            type="number"
            min={0}
            max={film.duration}
            step={0.1}
            value={still}
            onChange={(event) => setStill(Number(event.target.value))}
          />{" "}
          seconds
        </label>{" "}
        <button
          type="button"
          className="button"
          onClick={() => {
            stillFrame(film, palette, recordingSite, format, still)
              .then((blob) => offer(blob, fileName(site, film, format, "png").replace("-film-", `-still-${clock(still).replace(":", "m")}s-`)))
              .catch((error: unknown) => setProblem(error instanceof Error ? error.message : "The frame could not be saved."));
          }}
        >
          Save a still (PNG)
        </button>{" "}
        {sound === undefined ? null : (
          <button
            type="button"
            className="button"
            onClick={() => {
              renderSoundtrack(film, recordingSite, sound)
                .then((buffer) => {
                  if (buffer !== undefined) {
                    offer(encodeWav(buffer), fileName(site, film, format, "wav").replace(`-film-${format.id}`, `-soundtrack-${mixId}`));
                  }
                })
                .catch((error: unknown) => setProblem(error instanceof Error ? error.message : "The soundtrack could not be saved."));
            }}
          >
            Save the soundtrack (WAV)
          </button>
        )}
      </p>
      {progress === undefined ? null : <progress max={1} value={progress} />}
      {problem === undefined ? null : <p className="notice">{problem}</p>}
      {saved === undefined ? null : (
        <p>
          <a className="cta" href={saved.href} download={saved.name}>
            Save {saved.name}
          </a>
        </p>
      )}
    </section>
  );
}
